/**
 * Kardex: todo lo que entró y salió, con el saldo que quedó después.
 *
 * El saldo se guarda en cada movimiento, no se recalcula al mostrar. Un kardex
 * cuyo pasado cambia cuando corriges algo de hoy no sirve para cuadrar un
 * inventario ni para responderle a una inspección.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, ScrollText } from "lucide-react";
import { listKardex, listProductos } from "../api/farmacia";
import { useZonaHoraria } from "../auth/TenantBrandingProvider";
import { fechaHora } from "../lib/fecha";
import { Badge, Card, CardBody, CardHeader, Field } from "../components/ui";

const ETIQUETA: Record<string, string> = {
  COMPRA: "Ingreso por compra",
  DEVOLUCION_CLIENTE: "Devolución",
  AJUSTE_POSITIVO: "Ajuste (+)",
  TRANSFERENCIA_ENTRADA: "Transferencia recibida",
  DISPENSACION: "Dispensación de receta",
  VENTA: "Venta en mostrador",
  AJUSTE_NEGATIVO: "Ajuste (−)",
  MERMA: "Merma",
  VENCIMIENTO: "Baja por vencimiento",
  TRANSFERENCIA_SALIDA: "Transferencia enviada",
};

export function FarmaciaKardexPage() {
  const tz = useZonaHoraria();
  const [productoId, setProductoId] = useState<number | "">("");

  const { data: productos = [] } = useQuery({
    queryKey: ["farmacia", "productos", ""],
    queryFn: () => listProductos({}),
  });
  const { data: movimientos = [], isLoading } = useQuery({
    queryKey: ["farmacia", "kardex", productoId],
    queryFn: () =>
      listKardex({ producto_id: productoId === "" ? undefined : productoId, limit: 300 }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
          <ScrollText className="h-6 w-6 text-[color:var(--color-link)]" />
          Kardex
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Movimientos de existencias, valorizados por <strong>PEPS</strong>.
          Nada se edita ni se borra: un error se corrige con otro movimiento.
        </p>
      </div>

      <Card>
        <CardHeader>
          <Field label="Producto" htmlFor="k_producto">
            <select
              id="k_producto"
              value={productoId}
              onChange={(e) =>
                setProductoId(e.target.value === "" ? "" : Number(e.target.value))
              }
              className="w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Todos los productos</option>
              {productos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre} {p.concentracion ?? ""} · stock {p.stock}
                </option>
              ))}
            </select>
          </Field>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && (
            <p className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</p>
          )}
          {!isLoading && movimientos.length === 0 && (
            <p className="px-6 py-10 text-center text-sm text-slate-500">
              Sin movimientos todavía.
            </p>
          )}
          {movimientos.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-6 py-2 font-medium">Fecha</th>
                    <th className="px-3 py-2 font-medium">Producto</th>
                    <th className="px-3 py-2 font-medium">Movimiento</th>
                    <th className="px-3 py-2 font-medium">Lote</th>
                    <th className="px-3 py-2 text-right font-medium">Cantidad</th>
                    <th className="px-3 py-2 text-right font-medium">Costo</th>
                    <th className="px-3 py-2 text-right font-medium">Saldo</th>
                    <th className="px-3 py-2 text-right font-medium">Saldo valorizado</th>
                    <th className="px-6 py-2 font-medium">Responsable</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {movimientos.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50/60">
                      <td className="whitespace-nowrap px-6 py-2 text-slate-500">
                        {fechaHora(m.fecha, tz)}
                      </td>
                      <td className="px-3 py-2 text-slate-900">{m.producto_nombre}</td>
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-1.5 text-slate-700">
                          {m.es_ingreso ? (
                            <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-600" />
                          ) : (
                            <ArrowUpRight className="h-3.5 w-3.5 text-red-600" />
                          )}
                          {ETIQUETA[m.tipo] ?? m.tipo}
                        </span>
                        {m.motivo && (
                          <span className="block text-xs text-slate-400">{m.motivo}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">
                        {m.lote_numero ?? "—"}
                        {m.lote_vencimiento ? ` · ${m.lote_vencimiento}` : ""}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono ${
                          m.es_ingreso ? "text-emerald-700" : "text-red-700"
                        }`}
                      >
                        {m.es_ingreso ? "+" : "−"}
                        {m.cantidad}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">
                        {m.costo_total == null
                          ? "—"
                          : `S/ ${Number(m.costo_total).toFixed(2)}`}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">
                        {m.saldo}
                      </td>
                      {/* Vacío en lo anterior a la valorización: preferimos un
                          hueco visible a un número reconstruido a ojo. */}
                      <td className="px-3 py-2 text-right font-mono text-slate-700">
                        {m.saldo_valorizado == null
                          ? "—"
                          : `S/ ${Number(m.saldo_valorizado).toFixed(2)}`}
                      </td>
                      <td className="px-6 py-2 text-xs text-slate-500">
                        {m.registrado_por_nombre ?? <Badge variant="neutral">Sistema</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
